import {inject, Injectable, isDevMode} from '@angular/core';
import {IGlobalSearchDataState, initialGlobalSearchDataState, ISearchDataState} from './global-search-data.state';
import {SignalStore} from '@feature-signal-store';
import {useDevtools} from 'signalstory';
import {GlobalSearchApiService} from '../services/global-search-api.service';
import {BuilderByCategoryInstance, GlobalSearchCategory, PagedResult, SearchResultItem} from '../types/global-search.types';
import {catchError, EMPTY, tap} from 'rxjs';
import {FILTERS_CONFIG} from '../configs/filter-config';
import {AbstractQueryBuilder} from '@global-data';
import {CycleQueryBuilder} from '@cycles/data-access';
import {UserGroupsQueryBuilder, UsersQueryBuilder} from '@data-users';

@Injectable({providedIn: 'root'})
export class GlobalSearchDataStore extends SignalStore<IGlobalSearchDataState> {
	// --- Selectors (נשארים זהים) ---
	readonly cyclesState$ = this.select(state => state.cycles);
	readonly usersState$ = this.select(state => state.users);
	readonly userGroupsState$ = this.select(state => state.userGroups);
	private readonly apiService = inject(GlobalSearchApiService);
	private readonly filtersConfig = FILTERS_CONFIG;

	constructor() {
		super('Global Search Date Store', 'Global', initialGlobalSearchDataState, {
			// כלי ניפוי שגיאות רק במצב פיתוח
			plugins: isDevMode() ? [useDevtools()] : [],
		});
	}

	// Helper to get an active category state based on a passed category (since we don't inject UiStore anymore)
	public getStateForCategory(category: GlobalSearchCategory): ISearchDataState<any> {
		const key = this.getCategoryStateKey(category);
		return this.state()[key];
	}

	// Selector to get the status of a specific category
	getCategoryStatus$(category: GlobalSearchCategory) {
		return this.select(state => state[this.getCategoryStateKey(category)].status);
	}

	// Selector to get items of a specific category
	getCategoryItems$(category: GlobalSearchCategory) {
		return this.select(state => state[this.getCategoryStateKey(category)].items);
	}

	// --- Public Method to Trigger Search (Called by UiStore) ---
	/**
	 * @description
	 * This method builds a query based on the category and term, applies filters, and fetches results from the API.
	 * It updates the state with the results or an error if the fetch fails.
	 * @param category - The category to search in (e.g., cycles, users, user groups).
	 * @param term  - The search term to look for.
	 * @param filters - The filters to apply to the search.
	 * @param page - The page number to fetch.
	 * @param size - The number of results per page.
	 * @param overrideData - Whether to override existing data (default: true). [for loadMore use false to append the new data]
	 */
	fetchSearchResults(category: GlobalSearchCategory, term: string, filters: Record<string, any>, page: number, size: number, overrideData: boolean = true): void {
		if (!category) {
			console.warn('fetchSearchResults called without a category.');
			return;
		}

		this.setLoading(category, page);
		const builder = this.buildQuery(category, term, filters);

		this.apiService
			.search(category, builder, page, size)
			.pipe(
				tap((results: PagedResult<any>) => this.setResults(category, results, overrideData)),
				catchError(error => {
					console.error(`[${category}] Search/Page load failed:`, error);
					this.setError(category, error);
					return EMPTY; // Prevent stream from completing on error
				}),
			)
			.subscribe(); // We need to subscribe here to trigger the API call
	}

	// --- Public Method to Update Page Size ---
	setPageSize(category: GlobalSearchCategory, size: number): void {
		const categoryKey = this.getCategoryStateKey(category);
		this.update(
			state => ({
				...state,
				[categoryKey]: {
					...state[categoryKey],
					page: 1, // Reset to first page
					size: size,
				},
			}),
			`[${category}] Set Page Size`,
		);
		// Note: This only updates the state. The UiStore will need to trigger a refetch.
	}

	// --- Clear State Method ---
	clearCategoryState(category: GlobalSearchCategory): void {
		const key = this.getCategoryStateKey(category);
		const initialCategoryState = initialGlobalSearchDataState[key];
		this.patchState({[key]: initialCategoryState}, `[${category}] Clear State`);
	}

	clearAllSearchState(): void {
		this.patchState(
			{
				cycles: initialGlobalSearchDataState.cycles,
				users: initialGlobalSearchDataState.users,
				userGroups: initialGlobalSearchDataState.userGroups,
			},
			'Clear All Search State',
		);
	}

	public buildQuery<C extends GlobalSearchCategory>(category: C, searchTerm: string, filters: Record<string, any>): BuilderByCategoryInstance<C> | null {
		let builder: AbstractQueryBuilder<any, any>;
		const filterDefs = this.filtersConfig[category] ?? [];

		// Extract sort config from filters (if present)
		const sortConfig = filters.__sort;
		delete filters.__sort; // Remove from filters to not interfere with actual filters

		switch (category) {
			case GlobalSearchCategory.CYCLE:
				builder = CycleQueryBuilder.create();
				break;
			case GlobalSearchCategory.USER:
				builder = UsersQueryBuilder.create().setIncludePermanent(true);
				break;
			case GlobalSearchCategory.USER_GROUP:
				builder = UserGroupsQueryBuilder.create();
				break;
			default:
				console.error('Invalid category for query builder:', category);
				return null;
		}

		// Apply sorting if provided
		if (sortConfig) {
			if (sortConfig.sortBy) {
				builder.setSortBy(sortConfig.sortBy);
			}
			if (sortConfig.sortDirection) {
				builder.setSortDesc(sortConfig.sortDirection === 'desc');
			}
		} else {
			// Default sorting if no sort config is provided
			switch (category) {
				case GlobalSearchCategory.CYCLE:
					builder.setSortDesc(true);
					break;
				case GlobalSearchCategory.USER:
					builder.setSortBy('default');
					break;
			}
		}

		builder.setSearch(searchTerm || '');

		for (const filterType in filters) {
			if (Object.prototype.hasOwnProperty.call(filters, filterType)) {
				// Safer loop check
				const filterValue = filters[filterType];
				const definition = filterDefs.find(def => def.type === filterType);
				// Check applyFilter exists AND value is not explicitly undefined
				// (Allow null or false to be passed to applyFilter for clearing/setting boolean)
				if (definition?.applyFilter && filterValue !== undefined) {
					try {
						definition.applyFilter(builder, filterValue);
					} catch (e) {
						console.error(`Error applying filter ${filterType} with value`, filterValue, e);
					}
				}
			}
		}

		console.log('Built Query Builder:', builder, 'Markers:', builder.markers());
		return builder as BuilderByCategoryInstance<C>;
	}

	// --- Private Methods for State Updates (נשארים זהים או דומים) ---
	private setLoading(category: GlobalSearchCategory, page: number = 1): void {
		const key = this.getCategoryStateKey(category);
		this.patchState(
			state => ({
				[key]: {
					...state[key],
					status: 'loading',
					error: undefined,
					page: page, // Set page being loaded
				},
			}),
			`[${category}] Set Loading`,
		);
	}

	private setResults<T extends SearchResultItem>(category: GlobalSearchCategory, result: PagedResult<T>, overrideData: boolean): void {
		const key = this.getCategoryStateKey(category);
		this.patchState(
			state => ({
				[key]: {
					...state[key],
					items: overrideData ? result.items : [...state[key].items, ...result.items],
					total: result.total ?? result.items.length,
					status: 'idle',
					error: undefined,
				},
			}),
			`[${category}] Set Results`,
		);
	}

	private setError(category: GlobalSearchCategory, error: unknown): void {
		const key = this.getCategoryStateKey(category);
		this.patchState(
			state => ({
				[key]: {
					...state[key],
					status: 'error',
					error: error,
					items: [],
					total: 0,
				},
			}),
			`[${category}] Set Error`,
		);
	}

	private getCategoryStateKey(category: GlobalSearchCategory): keyof Omit<IGlobalSearchDataState, 'loadedQueries'> {
		switch (category) {
			case GlobalSearchCategory.CYCLE:
				return 'cycles';
			case GlobalSearchCategory.USER:
				return 'users';
			case GlobalSearchCategory.USER_GROUP:
				return 'userGroups';
			default:
				return 'cycles'; // Provide a default or handle error appropriately
		}
	}
}
